import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Loader2, FileText, RefreshCw } from "lucide-react";

/**
 * Phase 5.2 — File detail page for super-admin.
 *
 * Route: /admin/users/:userId/files/:fileId
 * Shows status timeline, sizing, classified profile, chunk previews. Provides
 * a "Re-embed this file" button for ops use.
 */
function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_STEPS = ["uploaded", "extracting", "chunking", "embedding", "ready"];

function StatusTimeline({ status }) {
  const currentIdx = STATUS_STEPS.indexOf((status || "").toLowerCase());
  return (
    <div className="flex items-center gap-1 mt-3" data-testid="file-status-timeline">
      {STATUS_STEPS.map((s, i) => {
        const isDone = currentIdx >= 0 && i <= currentIdx;
        const isCurrent = i === currentIdx;
        return (
          <React.Fragment key={s}>
            <div
              className={`px-2 py-1 rounded-sm border text-[10px] uppercase font-mono tracking-wider ${
                isCurrent
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                  : isDone
                  ? "border-emerald-300 bg-emerald-50/40 text-emerald-700 dark:text-emerald-400"
                  : "border-border text-muted-foreground"
              }`}
            >
              {s}
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <span className={`h-px w-3 ${isDone ? "bg-emerald-400" : "bg-border"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function AdminFileDetail() {
  const { userId, fileId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reembedding, setReembedding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/files/${fileId}`);
      setDoc(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load file");
      setDoc(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [userId, fileId]);

  const reembed = async () => {
    if (!window.confirm("Re-run extraction → chunking → embedding for this file? Existing chunks will be replaced.")) return;
    setReembedding(true);
    try {
      // Reuse the existing user-file re-embed endpoint if it exists; fall back
      // to the per-file admin re-run.
      await api.post(`/admin/users/${userId}/files/${fileId}/reembed`);
      toast.success("Re-embed queued");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-embed failed");
    } finally {
      setReembedding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="file-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading file…
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="min-h-screen p-10" data-testid="file-detail-not-found">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <p className="text-muted-foreground font-mono text-sm">File not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 lg:p-10 max-w-6xl mx-auto" data-testid="file-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <Link to="/admin" className="hover:underline">admin</Link>
          <span>/</span>
          <Link to={`/admin/users/${userId}`} className="hover:underline">users / {userId.slice(0, 8)}…</Link>
          <span>/</span>
          <span className="text-foreground">files / {fileId.slice(0, 8)}…</span>
        </div>
        <Button variant="ghost" onClick={() => navigate(`/admin/users/${userId}`)} data-testid="file-detail-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to user
        </Button>
      </div>

      {/* Title */}
      <div className="flex items-start gap-3 mb-2">
        <FileText className="h-5 w-5 mt-1 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-bold text-2xl tracking-tight break-all">{doc.name}</h1>
          <div className="text-[11px] font-mono text-muted-foreground mt-1">
            {doc.id}
          </div>
        </div>
      </div>

      <StatusTimeline status={doc.status} />

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <Kpi label="Status"   value={doc.status || "—"}                  testid="kpi-status" />
        <Kpi label="Size"     value={fmtBytes(doc.size_bytes)}           testid="kpi-size" />
        <Kpi label="Chunks"   value={doc.chunk_count ?? "—"}             testid="kpi-chunks" />
        <Kpi label="Profile"  value={doc.profile || "—"}                 testid="kpi-profile" highlight={!!doc.profile} />
      </div>

      {/* Metadata block */}
      <div className="border border-border bg-card rounded-sm p-4 mt-6" data-testid="file-detail-meta">
        <h2 className="text-sm font-bold uppercase tracking-wider font-mono mb-3">Metadata</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs font-mono">
          <Field label="Kind"        value={doc.kind} />
          <Field label="MIME"        value={doc.mime_type} />
          <Field label="Pages"       value={doc.page_count} />
          <Field label="Sub-status"  value={doc.sub_status} />
          <Field label="Created"     value={doc.created_at ? new Date(doc.created_at).toLocaleString() : "—"} />
          <Field label="Updated"     value={doc.updated_at ? new Date(doc.updated_at).toLocaleString() : "—"} />
          <Field label="Progress"    value={doc.progress_pct != null ? `${doc.progress_pct}%` : "—"} />
          <Field label="Lock model"  value={doc.embedding_lock?.model_id || "—"} />
        </div>
        {doc.error_message && (
          <div className="mt-3 p-2 rounded-sm border border-red-300 bg-red-50 text-xs font-mono text-red-800 dark:bg-red-950/30 dark:text-red-300">
            {doc.error_message}
          </div>
        )}
      </div>

      {/* Extracted preview */}
      {doc.extracted_content && (
        <div className="border border-border bg-card rounded-sm p-4 mt-6" data-testid="file-detail-extracted">
          <h2 className="text-sm font-bold uppercase tracking-wider font-mono mb-3">Extracted content preview</h2>
          <pre className="whitespace-pre-wrap text-xs font-mono text-muted-foreground max-h-72 overflow-y-auto">
            {doc.extracted_content}
          </pre>
        </div>
      )}

      {/* Chunks preview */}
      {Array.isArray(doc.chunks_preview) && doc.chunks_preview.length > 0 && (
        <div className="border border-border bg-card rounded-sm p-4 mt-6" data-testid="file-detail-chunks">
          <h2 className="text-sm font-bold uppercase tracking-wider font-mono mb-3">
            Chunks ({doc.chunk_count} total · showing {doc.chunks_preview.length})
          </h2>
          <div className="space-y-3">
            {doc.chunks_preview.map((c) => (
              <div key={c.index} className="border border-border rounded-sm p-3 bg-background" data-testid={`chunk-preview-${c.index}`}>
                <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground mb-1.5 flex-wrap">
                  <span className="font-bold text-foreground">#{c.index}</span>
                  {c.block_type && <span className="px-1.5 py-0.5 border border-border rounded-sm uppercase">{c.block_type}</span>}
                  {c.token_estimate != null && <span>{c.token_estimate} tok</span>}
                  {Array.isArray(c.headings) && c.headings.length > 0 && (
                    <span className="text-amber-700 dark:text-amber-300">[{c.headings.join(" › ")}]</span>
                  )}
                </div>
                <pre className="whitespace-pre-wrap text-xs font-mono text-foreground/90">{c.text}</pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-8 flex items-center gap-3">
        <Button onClick={reembed} disabled={reembedding} data-testid="file-detail-reembed">
          {reembedding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Re-embed this file
        </Button>
        <Button variant="ghost" onClick={load} data-testid="file-detail-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>
    </div>
  );
}

function Kpi({ label, value, testid, highlight }) {
  return (
    <div className="border border-border bg-card rounded-sm p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">{label}</div>
      <div className={`mt-1 text-base font-display font-bold tracking-tight ${highlight ? "text-amber-700 dark:text-amber-300" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-muted-foreground uppercase tracking-wider text-[10px]">{label}</span>
      <span className="text-foreground break-all">{value || "—"}</span>
    </div>
  );
}
